import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, getErrorMessage } from '../services/api';
import { Runner, RunnerStats, DCAState, ScriptedRunnerState, ExchangeCredential } from '../types';

export function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [runners, setRunners] = useState<Runner[]>([]);
  const [runnerStats, setRunnerStats] = useState<Record<number, RunnerStats | null>>({});
  const [credentials, setCredentials] = useState<ExchangeCredential[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Create a lookup map for credential ID to credential info
  const credentialMap = credentials.reduce((acc, cred) => {
    acc[cred.exchangeCredentialId] = cred;
    return acc;
  }, {} as Record<number, ExchangeCredential>);

  const loadRunnerStats = useCallback(async (runnerList: Runner[]) => {
    const statsPromises = runnerList.map(async (runner) => {
      try {
        const stateResponse = await api.getRunnerState(runner.runnerId);
        console.log(`Runner ${runner.runnerId} stateResponse:`, stateResponse);

        if (stateResponse?.stateJson) {
          const rawState = JSON.parse(stateResponse.stateJson) as Record<string, unknown>;
          console.log(`Runner ${runner.runnerId} parsed state:`, rawState);

          // Check if this is a scripted runner state (has scriptState property)
          if (rawState.scriptState !== undefined) {
            const scriptState = (rawState.scriptState || {}) as Record<string, unknown>;
            const lastPrice = (rawState.lastPrice as number) || 0;

            // Support multiple field naming conventions that scripts might use
            const totalQuantity =
              (scriptState.totalQuantity as number) ||
              (scriptState.position as number) ||
              (scriptState.quantity as number) ||
              0;
            const totalCost =
              (scriptState.totalCost as number) ||
              (scriptState.cost as number) ||
              0;
            const averagePrice =
              (scriptState.averagePrice as number) ||
              (scriptState.avgPrice as number) ||
              (totalQuantity > 0 ? totalCost / totalQuantity : 0);

            const currentValue = totalQuantity * lastPrice;
            const unrealizedPnL = currentValue - totalCost;
            const unrealizedPnLPercent = totalCost > 0 ? (unrealizedPnL / totalCost) * 100 : 0;

            return {
              runnerId: runner.runnerId,
              stats: {
                currentPosition: totalQuantity,
                currentPrice: lastPrice,
                currentValue: currentValue,
                unrealizedPnL: unrealizedPnL,
                unrealizedPnLPercent: unrealizedPnLPercent,
                averagePrice: averagePrice,
                lastUpdated: stateResponse.lastUpdated,
                basePrecision: stateResponse.basePrecision,
                quotePrecision: stateResponse.quotePrecision,
              } as RunnerStats,
            };
          }

          // Standard DCA state format
          const state = rawState as unknown as DCAState;
          return {
            runnerId: runner.runnerId,
            stats: {
              currentPosition: state.totalQuantityHeld,
              currentPrice: state.lastPrice,
              currentValue: state.currentValue,
              unrealizedPnL: state.unrealizedPnL,
              unrealizedPnLPercent: state.unrealizedPnLPercent,
              averagePrice: state.averagePrice,
              lastUpdated: stateResponse.lastUpdated,
              basePrecision: stateResponse.basePrecision,
              quotePrecision: stateResponse.quotePrecision,
            } as RunnerStats,
          };
        }
        return { runnerId: runner.runnerId, stats: null };
      } catch (err) {
        console.error(`Error loading stats for runner ${runner.runnerId}:`, err);
        return { runnerId: runner.runnerId, stats: null };
      }
    });

    const results = await Promise.all(statsPromises);
    const statsMap: Record<number, RunnerStats | null> = {};
    results.forEach(({ runnerId, stats }) => {
      statsMap[runnerId] = stats;
    });
    setRunnerStats(statsMap);
  }, []);

  const loadRunners = useCallback(async () => {
    try {
      setIsLoading(true);
      // Load runners and credentials in parallel
      const [runnersData, credsData] = await Promise.all([
        api.getRunners(),
        api.getExchangeCredentials(),
      ]);
      setRunners(runnersData);
      setCredentials(credsData);
      setError('');
      // Load stats for all runners
      await loadRunnerStats(runnersData);
    } catch (err: unknown) {
      setError('Failed to load runners');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [loadRunnerStats]);

  useEffect(() => {
    void loadRunners();
  }, [loadRunners]);

  // Poll for stats updates for active runners
  useEffect(() => {
    const activeRunners = runners.filter(r => r.status === 'Active');
    if (activeRunners.length === 0) return;

    const interval = setInterval(() => {
      void loadRunnerStats(runners);
    }, 10000); // Update every 10 seconds

    return () => clearInterval(interval);
  }, [runners, loadRunnerStats]);

  const handleStartRunner = (runnerId: number) => {
    api.startRunner(runnerId)
      .then(() => loadRunners())
      .catch((err: unknown) => {
        setError(`Failed to start runner: ${getErrorMessage(err)}`);
      });
  };

  const handleStopRunner = (runnerId: number) => {
    api.stopRunner(runnerId)
      .then(() => loadRunners())
      .catch((err: unknown) => {
        setError(`Failed to stop runner: ${getErrorMessage(err)}`);
      });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return '#4caf50';
      case 'Paused': return '#ff9800';
      case 'Inactive': return '#9e9e9e';
      default: return '#9e9e9e';
    }
  };

  const getTypeColor = (type: string) => {
    return type === 'Spot' ? '#2196f3' : '#9c27b0';
  };

  const getPnLColor = (pnl: number | null | undefined) => {
    if (pnl === null || pnl === undefined) return '#8892b0';
    if (pnl > 0) return '#4caf50';
    if (pnl < 0) return '#f44336';
    return '#8892b0';
  };

  const formatNumber = (num: number | null | undefined, decimals = 2) => {
    if (num === null || num === undefined) return '-';
    if (num === 0) return '0';
    if (Math.abs(num) < 0.01) return num.toFixed(6);
    return num.toFixed(decimals);
  };

  const formatCurrency = (num: number | null | undefined, currency: string, precision = 2) => {
    if (num === null || num === undefined) return '-';
    return `${formatNumber(num, precision)} ${currency}`;
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.logo}>Uliji</h1>
        <div style={styles.userInfo}>
          <button onClick={() => navigate('/docs')} style={styles.docsButton}>Docs</button>
          <button onClick={() => navigate('/settings')} style={styles.settingsButton}>Settings</button>
          <span style={styles.username}>{user?.username}</span>
          <button onClick={logout} style={styles.logoutButton}>Logout</button>
        </div>
      </header>

      <main style={styles.main}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Runners</h2>
          <div style={styles.headerActions}>
            <button onClick={() => { void navigate('/runner/new'); }} style={styles.newButton}>
              + New Runner
            </button>
            <button onClick={() => { void loadRunners(); }} style={styles.refreshButton}>Refresh</button>
          </div>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {isLoading ? (
          <div style={styles.loading}>Loading...</div>
        ) : runners.length === 0 ? (
          <div style={styles.empty}>
            <p>No runners configured</p>
            <button onClick={() => { void navigate('/runner/new'); }} style={styles.emptyButton}>
              Create your first runner
            </button>
          </div>
        ) : (
          <div style={styles.runnersGrid}>
            {runners.map((runner) => (
              <div key={runner.runnerId} style={styles.runnerCard}>
                <div style={styles.runnerHeader}>
                  <span style={styles.runnerName}>{runner.displayName}</span>
                  <span style={{
                    ...styles.statusBadge,
                    backgroundColor: getStatusColor(runner.status),
                  }}>
                    {runner.status}
                  </span>
                </div>

                <div style={styles.runnerDetails}>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Symbol</span>
                    <span style={styles.detailValue}>{runner.symbol}</span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Exchange</span>
                    <span style={styles.detailValue}>
                      {runner.exchangeCredentialId
                        ? credentialMap[runner.exchangeCredentialId]?.exchangeName || 'Unknown'
                        : 'Not configured'}
                    </span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Type</span>
                    <span style={{
                      ...styles.typeBadge,
                      backgroundColor: getTypeColor(runner.runnerType),
                    }}>
                      {runner.runnerType}
                    </span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Strategy</span>
                    <span style={styles.detailValue}>{runner.className}</span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Investment</span>
                    <span style={styles.detailValue}>
                      {runner.startingInvestment} {runner.baseCurrency}
                    </span>
                  </div>
                  {runner.intervalHours && (
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Interval</span>
                      <span style={styles.detailValue}>{runner.intervalHours}h</span>
                    </div>
                  )}
                </div>

                {/* Stats Section */}
                {(() => {
                  const stats = runnerStats[runner.runnerId];
                  if (!stats) return null;
                  return (
                    <div style={styles.statsSection}>
                      <div style={styles.statsSectionTitle}>Live Stats</div>
                      <div style={styles.statsGrid}>
                        <div style={styles.statItem}>
                          <span style={styles.statLabel}>Position</span>
                          <span style={styles.statValue}>
                            {formatNumber(stats.currentPosition, stats.basePrecision)}
                          </span>
                        </div>
                        <div style={styles.statItem}>
                          <span style={styles.statLabel}>Price</span>
                          <span style={styles.statValue}>
                            {formatCurrency(stats.currentPrice, runner.baseCurrency, stats.quotePrecision)}
                          </span>
                        </div>
                        <div style={styles.statItem}>
                          <span style={styles.statLabel}>Value</span>
                          <span style={styles.statValue}>
                            {formatCurrency(stats.currentValue, runner.baseCurrency, stats.quotePrecision)}
                          </span>
                        </div>
                        <div style={styles.statItem}>
                          <span style={styles.statLabel}>P&L</span>
                          <span style={{
                            ...styles.statValue,
                            color: getPnLColor(stats.unrealizedPnL),
                          }}>
                            {stats.unrealizedPnL >= 0 ? '+' : ''}
                            {formatCurrency(stats.unrealizedPnL, runner.baseCurrency, stats.quotePrecision)}
                            {' '}
                            ({stats.unrealizedPnLPercent >= 0 ? '+' : ''}
                            {formatNumber(stats.unrealizedPnLPercent)}%)
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <div style={styles.runnerActions}>
                  {runner.status === 'Inactive' && (
                    <div style={styles.actionButtons}>
                      <button
                        onClick={() => handleStartRunner(runner.runnerId)}
                        style={styles.startButton}
                      >
                        Start
                      </button>
                      <button
                        onClick={() => { void navigate(`/runner/${runner.runnerId}/detail`); }}
                        style={styles.viewButton}
                      >
                        View
                      </button>
                      <button
                        onClick={() => { void navigate(`/runner/${runner.runnerId}`); }}
                        style={styles.editButton}
                      >
                        Edit
                      </button>
                    </div>
                  )}
                  {runner.status === 'Active' && (
                    <div style={styles.actionButtons}>
                      <button
                        onClick={() => handleStopRunner(runner.runnerId)}
                        style={styles.stopButton}
                      >
                        Stop
                      </button>
                      <button
                        onClick={() => { void navigate(`/runner/${runner.runnerId}/detail`); }}
                        style={styles.viewButton}
                      >
                        View
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1a1a2e',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 32px',
    backgroundColor: '#16213e',
    borderBottom: '1px solid #233554',
  },
  logo: {
    color: '#e94560',
    fontSize: '24px',
    fontWeight: 'bold',
    margin: 0,
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  username: {
    color: '#ccd6f6',
  },
  logoutButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #e94560',
    backgroundColor: 'transparent',
    color: '#e94560',
    cursor: 'pointer',
    fontSize: '14px',
  },
  settingsButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #8892b0',
    backgroundColor: 'transparent',
    color: '#8892b0',
    cursor: 'pointer',
    fontSize: '14px',
  },
  docsButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #64ffda',
    backgroundColor: 'transparent',
    color: '#64ffda',
    cursor: 'pointer',
    fontSize: '14px',
  },
  main: {
    padding: '32px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  sectionTitle: {
    color: '#ccd6f6',
    fontSize: '24px',
    margin: 0,
  },
  headerActions: {
    display: 'flex',
    gap: '12px',
  },
  newButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#e94560',
    color: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  refreshButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#233554',
    color: '#ccd6f6',
    cursor: 'pointer',
    fontSize: '14px',
  },
  error: {
    backgroundColor: 'rgba(233, 69, 96, 0.2)',
    border: '1px solid #e94560',
    color: '#e94560',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '24px',
  },
  loading: {
    color: '#8892b0',
    textAlign: 'center',
    padding: '48px',
  },
  empty: {
    color: '#8892b0',
    textAlign: 'center',
    padding: '48px',
    backgroundColor: '#16213e',
    borderRadius: '12px',
  },
  emptyButton: {
    marginTop: '16px',
    padding: '12px 24px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#e94560',
    color: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  runnersGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '24px',
  },
  runnerCard: {
    backgroundColor: '#16213e',
    borderRadius: '12px',
    padding: '24px',
    border: '1px solid #233554',
  },
  runnerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  runnerName: {
    color: '#ccd6f6',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  statusBadge: {
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '12px',
    color: 'white',
    fontWeight: 'bold',
  },
  typeBadge: {
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    color: 'white',
  },
  runnerDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  detailLabel: {
    color: '#8892b0',
    fontSize: '14px',
  },
  detailValue: {
    color: '#ccd6f6',
    fontSize: '14px',
  },
  runnerActions: {
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: '1px solid #233554',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  startButton: {
    flex: 1,
    padding: '10px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#4caf50',
    color: 'white',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  stopButton: {
    flex: 1,
    padding: '10px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#f44336',
    color: 'white',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  editButton: {
    padding: '10px 16px',
    borderRadius: '6px',
    border: '1px solid #8892b0',
    backgroundColor: 'transparent',
    color: '#8892b0',
    fontSize: '14px',
    cursor: 'pointer',
  },
  viewButton: {
    padding: '10px 16px',
    borderRadius: '6px',
    border: '1px solid #2196f3',
    backgroundColor: 'transparent',
    color: '#2196f3',
    fontSize: '14px',
    cursor: 'pointer',
  },
  runningText: {
    color: '#4caf50',
    fontSize: '14px',
    marginLeft: '8px',
  },
  statsSection: {
    marginTop: '16px',
    padding: '12px',
    backgroundColor: 'rgba(35, 53, 84, 0.5)',
    borderRadius: '8px',
  },
  statsSectionTitle: {
    color: '#8892b0',
    fontSize: '12px',
    textTransform: 'uppercase' as const,
    letterSpacing: '1px',
    marginBottom: '12px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  statLabel: {
    color: '#8892b0',
    fontSize: '11px',
    textTransform: 'uppercase' as const,
  },
  statValue: {
    color: '#ccd6f6',
    fontSize: '14px',
    fontWeight: 'bold' as const,
  },
};
